import React, { useEffect, useRef, useState } from 'react';
import {
  Dimensions, StyleSheet, Text, TextInput, View, Pressable, ScrollView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useSelector } from 'react-redux';
import SearchIcon from '../../components/SearchIcon';
import Colors from '../../constants/Colors';
import getTemplateGroups from '../../requests/Assets/GetTemplateGroups';
import { RootTabScreenProps } from '../../types';
import getTemplateSpreadsheet from '../../requests/Assets/GetTemplateSpreadsheet';
import PlusIcon from '../../components/16pxIcons/PlusIcon';
import AddTemplateGroupModal from '../../components/AssetManagement/AddTemplateGroupModal';
import AlertModal from '../../components/AlertModal';
import SearchResultsPane from '../../components/AssetManagement/SearchResultsPane';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import NoTemplateGroupsImage from '../../components/AssetManagement/NoTemplateGroupsImage';
import NoTemplatesImage from '../../components/AssetManagement/NoTemplatesImage';
import { logEvent } from '../../components/LogEvent';
import Style from '../../constants/Style';
import PressableStyled, { PressableState } from '../../components/PressableStyled';
import {
  AssetInstance, UserPermission, Template, TemplateGroup, UserGroup,
} from '../../constants/Types';
import getUserGroups from '../../requests/Users/GetUserGroups';
import {
  ATLEAST_ONE_USER_GROUP, DELETE_TEMPLATE_FAILURE, noPermissionsSubtitle, noPermissionsTitle,
} from '../../constants/AlertMessages';
import deleteTemplate from '../../requests/Assets/DeleteTemplate';
import CreateTemplate from './components/CreateTemplate';
import { openInNewTab } from '../../constants/Utils';
import ScrollViewStyled from '../../components/ScrollViewStyled';
import TemplatePresetModal from './components/TemplatePresetModal';
import {
  TemplateUpdateOption,
} from '../../components/AssetManagement/AddTemplateModal/components/CreationOptionList/components/CreationOption';
import SkeletonLoader from '../../components/SkeletonLoader';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import Button from '../../components/Button';
import { screenMaxWidth } from '../../components/TopSection';
import ExportButton from '../../components/ExportButton';
import ColumnsButton from '../../components/ColumnsButton';
import isMobile from '../../components/IsMobile';
import TemplatesMobileView, { TemplateItem } from './components/TemplatesMobileView';
import TemplateDetailsScreen from './components/TemplatesMobileView/components/TemplateDetailsScreen';
import TemplateRow from '../../components/AssetManagement/TemplateRow';
import TemplateGroupRow from './components/TemplateGroupRow';

export default function TemplateMgmtScreenV2({
  navigation, route,
}: RootTabScreenProps<AppScreen.TEMPLATE_MANAGEMENTV2>) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewTemplates: boolean = isAdmin || permissions?.includes(UserPermission.TEMPLATES_READ);
  const canAddTemplate: boolean = isAdmin || permissions?.includes(UserPermission.TEMPLATES_CREATE);
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(true);
  const [templateGroups,
    setTemplateGroups] = useState<TemplateGroup[] | undefined | null>(undefined);
  const [templates, setTemplates] = useState<Template[]>(null);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [exportDropdownShown, setExportDropdownShown] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(null);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<TemplateItem>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetInstance>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [addGroupModalVisible, setAddGroupModalVisible] = useState<boolean>(false);
  const [templateSideBarContext, setTemplateSideBarContext] = useState<string>(null);
  const [searchResultsPaneFocused, setSearchResultsPaneFocused] = useState<boolean>(false);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const [optionsMenuTemplateId, setOptionsMenuTemplateId] = useState<string | null>(null);
  const templateSearchField = useRef<TextInput | null>(null);
  const [addingTemplate, setAddingTemplate] = useState<boolean>(false);
  const [addTemplateOption, setAddTemplateOption] = useState<TemplateUpdateOption>(null);
  const [addTemplateId, setAddTemplateId] = useState<string | null>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | React.ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [noDataAlertShown, setNoDataAlertShown] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [sidebarShown, setSidebarShown] = useState<boolean>(true);
  const selectedTemplateGroup: TemplateGroup = templateGroups
    ?.filter((group: TemplateGroup) => group.ButtonPrototypeGroupId === selectedGroupId)[0];

  const onExportButtonPress: () => void = () => {
    if (isMobile) {
      exportData();
      return;
    }
    setTemplateSideBarContext(null);
    logEvent('AssetManagementWeb', 'Export');
    if (Object.values(buildings)?.length > 0) {
      setExportDropdownShown(!exportDropdownShown);
    } else exportData();
  };

  const showScreenAlert = (
    title: string,
    message?: string | React.ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const hideMenus: () => void = () => {
    setTemplateSideBarContext(null);
    setOptionsMenuTemplateId(null);
    setExportDropdownShown(false);
  };

  const exportError = (errorText: string) => {
    setExporting(false);
    showScreenAlert('Error', errorText ?? 'Unable to export data at the moment. Please try again later.');
  };

  const exportData = (buildingId?: string) => {
    setExporting(true);
    setExportDropdownShown(false);
    getTemplateSpreadsheet(buildingId).then((response) => {
      if (response && response.url?.length > 0) openInNewTab(response.url);
      else exportError(response?.errorText);
      setExporting(false);
    }).catch((error) => exportError(error?.message));
  };

  const noTemplatesFound: () => void = () => {
    if (!noDataAlertShown) {
      setTemplates([]);
      setTemplateGroups(null);
      showScreenAlert('Error', 'Could not load asset templates at the moment. Please try again later.');
      setNoDataAlertShown(true);
      setTemplatesLoading(false);
    }
  };

  const getSortedTemplateGroups = (updatedTemplateGroupList: TemplateGroup[]) => (
    [...updatedTemplateGroupList].sort((a: TemplateGroup, b: TemplateGroup) => a.Name
      ?.toLowerCase().localeCompare(b.Name?.toLowerCase()))
  );

  const setSortedTemplateGroups = (updatedTemplateGroupList: TemplateGroup[]) => {
    setTemplateGroups(getSortedTemplateGroups(updatedTemplateGroupList));
  };

  const updateTemplateGroupLocally = (
    Name: string,
    ButtonPrototypeGroupId: string,
    create: boolean,
    toDelete: boolean = false,
  ) => {
    setTemplateGroups((previousTemplateGroups) => {
      const updatedTemplateGroups = [...(previousTemplateGroups ?? [])];

      if (toDelete) {
        return getSortedTemplateGroups(updatedTemplateGroups.filter((group: TemplateGroup) => group
          .ButtonPrototypeGroupId !== ButtonPrototypeGroupId));
      }

      if (create) {
        updatedTemplateGroups.push({
          Name,
          ButtonPrototypeGroupId,
          buttonPrototypes: [],
        });
      } else {
        updatedTemplateGroups.forEach((group) => {
          if (group.ButtonPrototypeGroupId === ButtonPrototypeGroupId) {
            group.Name = Name;
          }
        });
      }

      return getSortedTemplateGroups(updatedTemplateGroups);
    });

    if (toDelete && ButtonPrototypeGroupId === selectedGroupId) setSelectedGroupId(null);
  };

  useEffect(() => {
    if (!exportDropdownShown) return;
    setTemplateSideBarContext(null);
    setOptionsMenuTemplateId(null);
  }, [exportDropdownShown]);

  useEffect(() => {
    if (!templateSideBarContext) return;
    setExportDropdownShown(false);
    setOptionsMenuTemplateId(null);
  }, [templateSideBarContext]);

  useEffect(() => {
    if (!optionsMenuTemplateId) return;
    setTemplateSideBarContext(null);
    setExportDropdownShown(false);
  }, [optionsMenuTemplateId]);

  useEffect(() => {
    getUserGroups().then((response: { UserGroups?: UserGroup[] }) => {
      setUserGroups(response?.UserGroups
        ?.sort((prev: UserGroup, next: UserGroup) => prev.Name.toLowerCase()
          .localeCompare(next.Name.toLowerCase())) ?? []);
    });
    getTemplateGroups().then((response) => {
      if (!response?.groups) noTemplatesFound();
      else {
        setSortedTemplateGroups(response?.groups);
        const templateDataList: Template[] = response?.groups?.reduce((prev, group) => {
          if (group?.buttonPrototypes?.length) {
            return prev.concat(group.buttonPrototypes.map((template: Template) => ({
              ...template,
              templateGroupName: group.Name,
            })));
          }
          return prev;
        }, []) ?? [];
        setTemplates(templateDataList
          .sort((prev: Template, next: Template) => prev.name?.toLowerCase()
            .localeCompare(next.name?.toLowerCase())));
      }
    }).catch(noTemplatesFound);
    const unsubscribe = navigation.addListener('blur', hideMenus);
    return unsubscribe;
  }, []);

  useEffect(() => {
    setSearchValue('');
  }, [selectedTemplate, selectedGroupId]);

  useEffect(hideMenus, [
    addingTemplate, selectedTemplate, selectedGroupId, searchValue, addGroupModalVisible]);

  useEffect(() => {
    if (templatesLoading && templates) {
      setTemplatesLoading(false);
    }
  }, [templates]);

  useEffect(() => {
    if (!(canViewTemplates && templates && !templatesLoading)) return;
    const templateId: string = (route?.params as any)?.templateId;

    if (templateId) {
      setSelectedTemplate(null);
      setAssets([]);
      if (templateId) {
        const filteredTemplate: Template | null = templates
          .filter((template: Template) => template.id === templateId)[0];
        if (filteredTemplate) {
          setSelectedTemplate(filteredTemplate);
          setAssets([]);
          setAssetsLoading(true);
        } else {
          setSelectedTemplate(null);
          showScreenAlert('Error', 'Linked template could not be found. It may have been deleted.');
        }
      }

      navigation.setParams({ templateId: undefined, assetId: undefined } as any);
    }
  }, [route, templates, templatesLoading]);

  useEffect(() => {
    if (!(selectedAssetId && selectedTemplate && !assetsLoading)) return;
    const filteredAsset = assets
      ?.filter((asset: AssetInstance) => asset.id === selectedAssetId)[0];
    if (filteredAsset) setSelectedAsset(filteredAsset);
    else {
      showScreenAlert('Error', 'Linked asset could not be found. It may have been deleted.');
    }
    setSelectedAssetId(null);
  }, [selectedTemplate, selectedAssetId, assetsLoading]);

  const updateTemplateLocally = (template: Template, add: boolean, groupID: string) => {
    if (!template?.id) return;
    setTemplatesLoading(true);
    const templateGroupName: string = templateGroups
      ?.find((group: TemplateGroup) => group.ButtonPrototypeGroupId === groupID)?.Name
      ?? (template as TemplateItem)?.templateGroupName
      ?? '';
    const updatedTemplate: TemplateItem = {
      ...(template as TemplateItem),
      templateGroupName,
    };

    if (add) {
      setTemplateGroups(templateGroups.map((group: TemplateGroup) => ({
        ...group,
        buttonPrototypes: group.ButtonPrototypeGroupId === groupID
          ? [...group.buttonPrototypes, updatedTemplate] : group.buttonPrototypes,
      })));
      setTemplates([...(templates ?? []), updatedTemplate]);
    } else {
      const updatedTemplateGroups: TemplateGroup[] = templateGroups
        ?.map((group: TemplateGroup) => ({
          ...group,
          buttonPrototypes: group.buttonPrototypes.filter((t: Template) => t.id !== template.id),
        }));
      updatedTemplateGroups.forEach((group: TemplateGroup) => {
        if (group.ButtonPrototypeGroupId === groupID) {
          group.buttonPrototypes.push(updatedTemplate);
        }
      });
      setTemplateGroups(updatedTemplateGroups);
      setTemplates((templates ?? [])
        .map((t: Template) => ({ ...t, ...(t.id === template.id ? updatedTemplate : {}) })));
    }
    if (selectedTemplate && selectedTemplate.id === template.id) {
      setSelectedTemplate(updatedTemplate);
    }
  };

  const updateTemplateAndShowDetails = (template: Template, add: boolean, groupID: string) => {
    updateTemplateLocally(template, add, groupID);
    const templateGroupName: string = templateGroups
      ?.find((group: TemplateGroup) => group.ButtonPrototypeGroupId === groupID)?.Name
      ?? (template as TemplateItem).templateGroupName
      ?? '';
    setSelectedTemplate(null);
    setSelectedTemplateDetails({
      ...(template as TemplateItem),
      templateGroupName,
    });
  };

  const removeTemplateLocally = (id: string) => {
    setTemplateGroups((previousTemplateGroups) => {
      if (!previousTemplateGroups?.length) return previousTemplateGroups;
      return previousTemplateGroups.map((group: TemplateGroup) => ({
        ...group,
        buttonPrototypes: (group.buttonPrototypes ?? [])
          .filter((currentTemplate: Template) => currentTemplate.id !== id),
      }));
    });
    setTemplates((previousTemplates) => (previousTemplates ?? [])
      .filter((template: Template) => template.id !== id));
  };

  const attemptTemplateDeletion = (template: TemplateItem) => {
    deleteTemplate(template.id).then((response: any) => {
      if (response && !response.isError) {
        removeTemplateLocally(template.id);
        setSelectedTemplateDetails(null);
        showScreenAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{template.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
        );
      } else showScreenAlert('Error', response?.errorText ?? DELETE_TEMPLATE_FAILURE);
    }).catch((error) => showScreenAlert('Error', error?.message ?? DELETE_TEMPLATE_FAILURE));
  };

  const promptDeleteTemplate = (template: TemplateItem) => {
    showScreenAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{template.name}</Text>
      </>,
      true,
      () => attemptTemplateDeletion(template),
      'Delete',
    );
  };

  const showAssets = (template: Template) => {
    navigation.navigate(
      AppScreen.ASSET_MANAGEMENT as any,
      { templateId: template.id, templateName: template.name },
    );
  };

  const renderTemplatesFromList = (templatesList) => <ScrollView
    testID='template-list'
    style={styles.templateList}
  >
    {templatesList
      ?.sort((a: Template, b: Template) => a.name?.toLowerCase()
        .localeCompare(b.name?.toLowerCase())).map((template, index: number) => !template.deleted
          && <TemplateRow
            {...{
              templateGroups,
              template,
              userGroups,
              index,
              optionsMenuTemplateId,
              setOptionsMenuTemplateId,
            }}
            closeMenu={hideMenus}
            showParentAlert={showScreenAlert}
            update={updateTemplateLocally}
            setLinkAssetsView={() => { }}
            setSelectedTitle={() => { }}
            setSelected={() => setSelectedTemplate(template)}
            onViewDetails={(selected: Template) => {
              setSelectedTemplateDetails(selected as TemplateItem);
            }}
            deleteAction={removeTemplateLocally}
            key={`template_${template.id}`}
          />)}
    {!templatesLoading && templateGroups?.length > 0
      && !((selectedGroupId ? templateGroups.filter((group) => group.ButtonPrototypeGroupId
        === selectedGroupId)[0]?.buttonPrototypes : templates)?.length > 0)
      && <ScreenEmptyState
        image={<NoTemplatesImage />}
        title={selectedGroupId ? 'No templates in this template group' : 'Add your first template'}
        subtitle='Contact your system administrator to add a template to work with'
        adminSubtitle='Add a template to work with'
        actionAvailable={canAddTemplate}
        buttonTitle='Add template'
        buttonAction={() => {
          logEvent('AssetManagementWeb', 'add_template_button');
          if (!userGroups.length) {
            showScreenAlert(
              'No user group found',
              ATLEAST_ONE_USER_GROUP,
              true,
            );
          } else setAddingTemplate(true);
        }}
        style={styles.emptyState}
      />}
  </ScrollView>;

  const renderTemplates = () => {
    if (templatesLoading) {
      return <View style={{ marginHorizontal: 24 }}>
        <SkeletonLoader delay={100} />
        <SkeletonLoader delay={200} />
        <SkeletonLoader delay={300} />
        <SkeletonLoader delay={400} />
        <SkeletonLoader delay={500} />
        <SkeletonLoader delay={600} />
        <SkeletonLoader delay={700} />
      </View>;
    }
    if (!selectedGroupId) return renderTemplatesFromList(templates);
    return renderTemplatesFromList(
      templateGroups?.filter((group) => group.ButtonPrototypeGroupId === selectedGroupId)[0]
        ?.buttonPrototypes ?? templates,
    );
  };

  const renderSearchPane = () => (searchValue?.length > 0 && <SearchResultsPane
    focused={searchResultsPaneFocused}
    blur={templateSearchField?.current?.focus}
    {...{
      searchValue, templates, templateGroups, selectedAsset, selectedTemplate, setSearchValue,
    }}
    selectTemplate={(template: Template) => showAssets(template)}
    selectAsset={setSelectedAsset}
    selectTemplateGroup={setSelectedGroupId}
    showAssets={() => { }}
    selectedTemplateGroup={selectedGroupId}
    assetsShown={false}
  />);

  if (!canViewTemplates) {
    return <View style={styles.container}>
      <ScreenEmptyState
        title={noPermissionsTitle('templates')}
        subtitle={noPermissionsSubtitle('templates')}
        image={<NoTemplatesImage />}
      />
    </View>;
  }

  if (isMobile) {
    return <TemplatesMobileView
      templates={templates as TemplateItem[]}
      templatesLoading={templatesLoading}
      {...{ templateGroups }}
      selectedTemplate={selectedTemplate as TemplateItem}
      canAddTemplate={canAddTemplate}
      canAddTemplateGroup={canAddTemplate}
      userGroups={userGroups}
      updateTemplateLocally={updateTemplateLocally}
      updateTemplateGroupLocally={updateTemplateGroupLocally}
      removeTemplateLocally={removeTemplateLocally}
      showScreenAlert={showScreenAlert}
    />;
  }

  return <Pressable
    onPress={hideMenus}
    style={styles.container}
  >
    {selectedTemplateDetails && <TemplateDetailsScreen
      template={selectedTemplateDetails}
      templateGroups={templateGroups}
      userGroups={userGroups}
      onBack={() => setSelectedTemplateDetails(null)}
      canEditTemplate={canEditTemplates}
      canDeleteTemplate={canDeleteTemplates}
      onEditTemplate={(template: TemplateItem) => {
        setSelectedTemplateDetails(null);
        setSelectedTemplate(template);
      }}
      onDeleteTemplate={promptDeleteTemplate}
    />}
    {!selectedTemplateDetails && (addTemplateOption || selectedTemplate) && <CreateTemplate
      templateUpdateOption={selectedTemplate ? TemplateUpdateOption.EDIT : addTemplateOption}
      selectedTemplateId={selectedTemplate?.id ?? addTemplateId}
      hide={() => {
        setAddingTemplate(false);
        setAddTemplateOption(null);
        setSelectedTemplate(null);
      }}
      {...{
        templateGroups, userGroups, templates, selectedGroupId,
      }}
      users={[]}
      update={updateTemplateAndShowDetails}
      showParentAlert={showScreenAlert}
      templateToUpdate={selectedTemplate}
      deleteAction={removeTemplateLocally}
    />}
    {!selectedTemplateDetails && !(addTemplateOption || selectedTemplate) && <View
      style={{ flex: 1, maxWidth: screenMaxWidth - 74 }}
    >
      <View style={styles.screenHeader}>
        <View style={Style.row}>
          <Text style={styles.screenTitle}>
            {(isMobile && selectedGroupId) ? selectedTemplateGroup?.Name : 'Template management'}
          </Text>
          <ExportButton
            onPress={onExportButtonPress}
            {...{ exporting }}
            testID='template-mngmt-export-btn'
          />
          {exportDropdownShown && <View style={styles.exportDropdown}>
            <Pressable
              onPress={() => exportData(selectedBuildingId)}
              style={({ hovered, pressed }: PressableState) => [
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              testID='export-current-data-btn'
            >
              <Text style={styles.exportOptionText}>{'Export data for current building'}</Text>
            </Pressable>
            <Pressable
              onPress={() => exportData()}
              style={({ hovered, pressed }: PressableState) => [
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              testID='export-all-data-btn'
            >
              <Text style={styles.exportOptionText}>{'Export data for all buildings'}</Text>
            </Pressable>
          </View>}
        </View>
      </View>
      <Animatable.View
        animation={'fadeIn'}
        duration={300}
        style={styles.content}
      >
        {sidebarShown && <View style={styles.sidebar} testID='template-groups-sidebar'>
          <View style={styles.sidebarHeader}>
            <Text style={styles.templateGroupsTitle}>
              {'TEMPLATE GROUPS'}
            </Text>
            {!isMobile && canAddTemplate && <PressableStyled
              style={styles.addTemplateGroupsButton}
              onPress={() => {
                logEvent('AssetManagementWeb', 'Add_Template_Group');
                setAddGroupModalVisible(!addGroupModalVisible);
              }}
              testID='am-add-template-group-btn'
            >
              <PlusIcon color={Colors.light.hereworksBlue500} />
            </PressableStyled>}
            <AddTemplateGroupModal
              {...{ templateGroups }}
              isVisible={addGroupModalVisible}
              hide={() => setAddGroupModalVisible(false)}
              update={updateTemplateGroupLocally}
            />
          </View>
          {!isMobile && <View style={styles.separator} />}
          <ScrollViewStyled contentContainerStyle={{ flexDirection: 'column-reverse' }}>
            {templateGroups?.length > 0
              ? templateGroups?.sort((prev, next) => next.Name?.toLowerCase()
                .localeCompare(prev.Name?.toLowerCase()))
                .map((item: TemplateGroup) => <TemplateGroupRow
                  {...{
                    selectedGroupId, templateGroups, showScreenAlert,
                  }}
                  group={item}
                  onPress={() => {
                    if (selectedGroupId === item.ButtonPrototypeGroupId) setSelectedGroupId(null);
                    else setSelectedGroupId(item.ButtonPrototypeGroupId);
                  }}
                  contextMenuOpen={() => setTemplateSideBarContext(item.ButtonPrototypeGroupId)}
                  contextMenuClose={hideMenus}
                  currentContextMenu={templateSideBarContext}
                  update={(newName: string, id: string, create?: boolean, toDelete?: boolean) => {
                    if (create) {
                      setSortedTemplateGroups([
                        ...templateGroups,
                        { Name: newName, ButtonPrototypeGroupId: id, buttonPrototypes: [] },
                      ]);
                    } else if (toDelete) {
                      setSortedTemplateGroups(templateGroups?.filter((group: TemplateGroup) => group
                        .ButtonPrototypeGroupId !== id));
                      if (id === selectedGroupId) setSelectedGroupId(null);
                    } else {
                      setSortedTemplateGroups(templateGroups?.map((group: TemplateGroup) => (group
                        .ButtonPrototypeGroupId === id ? { ...group, Name: newName } : group)));
                    }
                  }}
                  key={`template_group_menu_item_${item.ButtonPrototypeGroupId}`}
                />) : <View style={styles.noTemplateGroupsMessage}>
                {templateGroups && <Text style={styles.noTemplatesSubtitle}>
                  {'No groups'}
                </Text>}
                {templateGroups === undefined && <Text
                  style={styles.noTemplatesSubtitle}
                  testID='template-groups-loading-state'
                >
                  {'Loading template groups...'}
                </Text>}
                {templateGroups === null && <Text
                  style={styles.noTemplatesSubtitle}
                  testID='template-groups-error-state'
                >
                  {'Unable to load template groups'}
                </Text>}
              </View>}
          </ScrollViewStyled>
        </View>}
        <View style={{ flex: 7 }}>
          <View style={styles.searchPaneContainer}>
            <View style={styles.searchBox}>
              <SearchIcon />
              <TextInput
                placeholder="Search"
                placeholderTextColor={Colors.light.hereworksBlack400}
                value={searchValue}
                style={styles.searchText}
                testID='template-management-search-input'
                ref={templateSearchField}
                onFocus={() => setSearchResultsPaneFocused(false)}
                onChangeText={(text: string) => {
                  setSearchValue(text);
                  setSearchResultsPaneFocused(false);
                }}
                onKeyPress={(e) => {
                  // @ts-ignore
                  if (e.code === 'ArrowDown') setSearchResultsPaneFocused(true);
                }}
              />
            </View>
            {canAddTemplate && <Button
              style={styles.createButton}
              onPress={() => {
                logEvent('AssetManagementWeb', 'Create_new_template');
                if (!userGroups.length) {
                  showScreenAlert(
                    'No user group found',
                    ATLEAST_ONE_USER_GROUP,
                    true,
                  );
                } else setAddingTemplate(true);
              }}
              title={'Add template'}
              children={<PlusIcon style={{ marginStart: 8 }} />}
              affirmative
              testID='add-template-btn'
            />}
          </View>
          {renderSearchPane()}
          <View style={styles.contentHeader}>
            <Text style={styles.templateGroupTitle}>
              {templateGroups?.filter((group: TemplateGroup) => group.ButtonPrototypeGroupId
                === selectedGroupId)[0]?.Name ?? 'All templates'}
            </Text>
            <ColumnsButton
              onPress={() => setSidebarShown(!sidebarShown)}
              tooltipText={sidebarShown ? 'Collapse sidebar' : 'Expand sidebar'}
              testID='collapse-template-groups-btn'
            />
          </View>
          <View style={styles.listHeader}>
            <Text style={[styles.headerText, { flex: 3 }]}>{'TEMPLATES'}</Text>
            <Text style={[styles.headerText, { flex: 4 }]}>{'ASSET COUNT'}</Text>
          </View>
          {renderTemplates()}
          {!templatesLoading && templateGroups?.length === 0 && <ScreenEmptyState
            image={<NoTemplateGroupsImage />}
            title='Start with a template group'
            subtitle={'In template groups you will collect your templates.\nContact your system administrator to create a template group'}
            adminSubtitle={'In template groups you will collect your templates.\nPlease create a template group'}
            actionAvailable={canAddTemplate}
            buttonTitle='Create template group'
            buttonAction={() => setAddGroupModalVisible(true)}
          />}
        </View>
      </Animatable.View>
    </View>}
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      setModalVisible={setAlertVisible}
      actionTitle={alertActionTitle || (userGroups.length ? '' : 'Go to user management')}
      action={alertAction ?? (userGroups.length ? null : () => {
        navigation.navigate(AppScreen.USER_MANAGEMENT as any);
      })}
      isMessage={!userGroups.length}
    />
    {!addTemplateOption && <TemplatePresetModal
      isVisible={addingTemplate}
      hide={() => setAddingTemplate(false)}
      {...{ templates, setAddTemplateOption }}
      setSelectedTemplateId={setAddTemplateId}
    />}
  </Pressable>;
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 40,
    paddingTop: 48,
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
    height: Dimensions.get('screen').height - 120,
    cursor: 'auto',
  },
  content: isMobile ? {
    backgroundColor: '#fff',
    marginVertical: 12,
    marginHorizontal: -24,
    flex: 1,
  } : {
    borderRadius: 16,
    marginVertical: 32,
    overflow: 'hidden',
    flexDirection: 'row',
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
  },
  screenHeader: {
    zIndex: 2,
    marginHorizontal: 8,
  },
  selectedTitle: {
    color: Colors.light.hereworksBlue500,
  },
  createButton: {
    width: 168,
    marginStart: 40,
  },
  searchBox: {
    height: 40,
    flex: 1,
    borderRadius: 24,
    backgroundColor: '#fff',
    borderColor: Colors.light.hereworksPaleBlue900,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    paddingStart: 12,
    padding: 4,
  },
  searchText: {
    flex: 1,
    alignSelf: 'stretch',
    color: Colors.light.hereworksBlack400,
    marginStart: 4,
    paddingStart: 8,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    alignItems: 'center',
    textAlignVertical: 'center',
    outlineWidth: 0,
    borderRadius: 24,
  },
  separator: {
    backgroundColor: Colors.light.hereworksBlue100,
    height: 1,
    width: '100%',
    marginBottom: 8,
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 16,
    letterSpacing: 0.1,
    color: '#FFF',
  },
  searchPaneContainer: {
    ...Style.row,
    paddingHorizontal: 24,
    height: 62,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  contentHeader: {
    ...Style.row,
    margin: 24,
  },
  noTemplatesSubtitle: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
  },
  noTemplateGroupsMessage: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
    paddingHorizontal: 12,
    paddingVertical: 16,
    margin: 16,
  },
  addTemplateGroupsButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    padding: 4,
    marginEnd: 18,
  },
  addTemplateGroupsButtonMobile: {
    flex: 0,
    height: 38,
    bottom: 24,
    flexDirection: 'row-reverse',
  },
  arrowIcon: {
    marginTop: 16,
    marginHorizontal: 8,
  },
  templateGroupsTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
    letterSpacing: 0,
    marginTop: 24,
    marginHorizontal: 24,
    marginBottom: isMobile ? 8 : 16,
  },
  exportDropdown: {
    position: 'absolute',
    top: 48,
    end: 0,
    backgroundColor: '#FFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
  },
  exportOptionText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  sidebar: {
    minWidth: 240,
    flex: 2,
    backgroundColor: '#fff',
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue100,
  },
  sidebarHeader: {
    ...Style.row,
    justifyContent: isMobile ? 'flex-start' : 'space-between',
  },
  screenTitle: {
    fontSize: 32,
    lineHeight: 48,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
  },
  templateGroupTitle: {
    fontSize: 22,
    lineHeight: 33,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    letterSpacing: 0,
  },
  listHeader: {
    ...Style.row,
    marginHorizontal: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopStartRadius: 8,
    borderTopEndRadius: 8,
    backgroundColor: Colors.light.hereworksBlue400,
  },
  templateList: {
    flex: 1,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    marginHorizontal: 24,
    borderBottomEndRadius: 8,
    borderBottomStartRadius: 8,
  },
  emptyState: {
    flex: 1,
    alignSelf: 'center',
    marginVertical: '10%',
  },
});
